import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";
import { pythonApiClient } from "../pythonApiClient";
import { getToken, removeToken, setRefreshToken, setToken } from "@/utils/tokenStorage";
import { QUERY_KEYS } from "../queryKeys";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useUserStore from "@/store/userStore";

function hasUsableAuthToken() {
  const token = getToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    return decoded.exp > Date.now() / 1000;
  } catch {
    return false;
  }
}

// Auth hooks
export const useLogin = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await pythonApiClient.post('/auth/login/', credentials);
      setToken(response?.authToken);
      setRefreshToken(response?.refreshToken);
      return response;
    },
  });
};

export const useGoogleBasic = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await pythonApiClient.post(
        "/auth/google-basic/",
        credentials
      );
    setToken(response?.authToken);
    setRefreshToken(response?.refreshToken);
      return response;
    },
  });
};

export const useRegister = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await pythonApiClient.post(
        "/auth/register/",
        credentials
      );
      setToken(response?.authToken);
      setRefreshToken(response?.refreshToken);
      return response;
    },
  });
};


export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await pythonApiClient.post(
        "/auth/verify-email/",
        credentials
      );
      return response;
    },
  });
};

export const useResendVerificationCode = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await pythonApiClient.post(
        "/auth/resend-verification-code/",
        credentials
      );
      return response;
    },
  });
};

export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await pythonApiClient.post(
        "/auth/request-password-reset/",
        credentials
      );
      return response;
    },
  });
};

export const usePasswordReset = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await pythonApiClient.post(
        "/auth/reset-password/",
        credentials
      );
      return response;
    },
  });
};

export const useValidateToken = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await pythonApiClient.post(
        "/auth/validate-token/",
        credentials
      );
      return response;
    },
  });
};


export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: async () => {
      const { authAdapter } = await import("@/utils/authAdapter");
      await authAdapter.logout();
    },
    onSuccess: () => {
      queryClient.clear();
      navigate("/auth/login");        
    },
    onError: (error) => {
      console.warn('Logout failed, but clearing local data:', error);
      queryClient.clear();
      navigate("/auth/login");
    }
  });
};

// User hooks
export const useUserProfile = ({ enabled = true } = {}) => {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: [QUERY_KEYS.USER_PROFILE],
    queryFn: async () => {
      const response = await pythonApiClient.get('/profile/get-profile/');
      return response.user; // Extract user object from response
    },
    enabled: enabled && isAuthenticated && hasUsableAuthToken(),
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      return status !== 401 && status !== 403 && failureCount < 2;
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (profileData) => {
      const response = await pythonApiClient.put('/profile/update-profile/', profileData);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.USER_PROFILE]);
    },
  });
};

export const useCompleteProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ phone, country }) => {
      const response = await pythonApiClient.put("/profile/complete-profile/", {
        phone,
        country,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.USER_PROFILE]);
    },
  });
};